import {
  type ActionReducerMapBuilder,
  type AsyncThunk,
  type PayloadAction,
  createSlice
} from '@reduxjs/toolkit';

import { getDealerById as getAdminDealerById } from './admin/adminDealers.slice';
import {
  getCategoryById as getAdminDictionaryCategoryById,
  getReasonById as getAdminDictionaryReasonById,
  getTemplateById as getAdminDictionaryTemplateById
} from './admin/adminDictionary.slice';
import {
  getEquipmentById as getAdminAutoEquipmentById,
  getPackageById as getAdminAutoPackageById,
  getPackageProductById as getAdminAutoPackageProductById,
  getStockById as getAdminAutoStockById,
  getUsedCarById as getAdminAutoUsedCarById
} from './admin/adminModels.slice';
import {
  getPositionById as getAdminPositionById,
  getRolesById as getAdminRolesById,
  getUsersById as getAdminUsersById
} from './admin/adminUsers.slice';
import { tokenValidate } from './auth.slice';
import { getOptimizedDictionaries } from './dict.slice';
import { getAdminFaqArticle, getAdminFaqPageType } from './faq.slice';
import { createNewOrder as autoCreditCreateNewOrder } from './newOrder.slice';
import { getUserAccount, getUserMenu } from './userInfo.slice';

export type TLoadingActionType =
  'fullscreen' | 'fullscreenBlur' | 'layout' | 'layoutBlur';

type TLoadingStore = Record<`${TLoadingActionType}Count`, number> & {
  loadingsWithText: { id: string; type: TLoadingActionType; text: string }[];
};

const getKey = (
  type: TLoadingActionType | undefined = 'fullscreen'
): `${TLoadingActionType}Count` => `${type}Count`;

const initialState: TLoadingStore = {
  fullscreenBlurCount: 0,
  fullscreenCount: 0,
  layoutBlurCount: 0,
  layoutCount: 0,
  loadingsWithText: []
};

export const loadingSlice = createSlice({
  name: 'loadingSlice',
  initialState,
  reducers: {
    startGlobalLoadingWithText: (
      state,
      action: PayloadAction<TLoadingStore['loadingsWithText'][number]>
    ) => {
      state.loadingsWithText.push(action.payload);
    },
    stopGlobalLoadingWithTextById: (state, action: PayloadAction<string>) => {
      state.loadingsWithText = state.loadingsWithText.filter(
        (loading) => loading.id !== action.payload
      );
    },
    startGlobalLoading: (
      state,
      action: PayloadAction<TLoadingActionType | undefined>
    ) => {
      const key = getKey(action.payload);
      state[key] += 1;
    },
    stopGlobalLoading: (
      state,
      action: PayloadAction<TLoadingActionType | undefined>
    ) => {
      const key = getKey(action.payload);
      state[key] = Math.max(state[key] - 1, 0);
    },
    resetGlobalLoading: (
      state,
      action: PayloadAction<TLoadingActionType | undefined>
    ) => {
      const key = getKey(action.payload);
      state[key] = 0;
    }
  },
  extraReducers: (builder) => {
    const addLoadingFor = addLoadingListeners(builder);

    addLoadingFor(tokenValidate, 'fullscreenBlur');
    addLoadingFor(getUserAccount, 'fullscreenBlur');

    addLoadingFor(getOptimizedDictionaries, 'fullscreenBlur');

    addLoadingFor(getUserMenu, 'fullscreenBlur');

    addLoadingFor(getAdminDealerById);

    addLoadingFor(getAdminAutoEquipmentById);
    addLoadingFor(getAdminAutoStockById);
    addLoadingFor(getAdminAutoUsedCarById);
    addLoadingFor(getAdminAutoPackageById);
    addLoadingFor(getAdminAutoPackageProductById);

    addLoadingFor(getAdminUsersById);
    addLoadingFor(getAdminPositionById);
    addLoadingFor(getAdminRolesById);

    addLoadingFor(getAdminDictionaryCategoryById);
    addLoadingFor(getAdminDictionaryReasonById);
    addLoadingFor(getAdminDictionaryTemplateById);

    addLoadingFor(getAdminFaqArticle);
    addLoadingFor(getAdminFaqPageType);

    addLoadingFor(autoCreditCreateNewOrder, 'fullscreen');
  }
});

/** Подписывает счётчик загрузки на pending / fulfilled / rejected переданного thunk */
const addLoadingListeners =
  (builder: ActionReducerMapBuilder<TLoadingStore>) =>
  (thunk: AsyncThunk<any, any, any>, loadingType?: TLoadingActionType) => {
    const key = getKey(loadingType ?? 'layout');
    builder
      .addCase(thunk.pending, (state) => {
        state[key] += 1;
      })
      .addCase(thunk.fulfilled, (state) => {
        state[key] = Math.max(state[key] - 1, 0);
      })
      .addCase(thunk.rejected, (state) => {
        state[key] = Math.max(state[key] - 1, 0);
      });
  };

export const {
  startGlobalLoadingWithText,
  stopGlobalLoadingWithTextById,
  startGlobalLoading,
  stopGlobalLoading,
  resetGlobalLoading
} = loadingSlice.actions;
